import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";
import { Checklist } from "../CustomComponents/ChecklistInterface";
import MultiSubheaderTableRowComponent from "../CustomComponents/SubheaderTableRowComponent";
import googleDriveAxios from "../../common/googleDriveAxios";

const FinishingQualityInspectionChecklist: React.FC<{
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  isDisabled: boolean;
}> = ({ chassisNo, setModel, model, setCheckedBy, isDisabled }) => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState<boolean>(false);
  const [checkItems, setCheckItems] = useState<Checklist[]>([]);
  const [externalChassisFrameItems, setExternalChassisFrameItems] = useState<
    Checklist[]
  >([]);
  const [externalFrontItems, setExternalFrontItems] = useState<Checklist[]>([]);
  const [externalRoadSideItems, setExternalRoadSideItems] = useState<
    Checklist[]
  >([]);
  const [externalRearItems, setExternalRearItems] = useState<Checklist[]>([]);
  const [externalDoorSideItems, setExternalDoorSideItems] = useState<
    Checklist[]
  >([]);
  const [externalTopAndUnderneathItems, setExternalTopAndUnderneathItems] =
    useState<Checklist[]>([]);
  const [internalEntranceItems, setInternalEntranceItems] = useState<
    Checklist[]
  >([]);
  const [internalEnsuiteItems, setInternalEnsuiteItems] = useState<Checklist[]>(
    []
  );
  const [internalShowerItems, setInternalShowerItems] = useState<Checklist[]>(
    []
  );
  const [internalKitchenItems, setInternalKitchenItems] = useState<Checklist[]>(
    []
  );
  const [internalDinnetteItems, setInternalDinnetteItems] = useState<
    Checklist[]
  >([]);
  const [internalBedroomItems, setInternalBedroomItems] = useState<Checklist[]>(
    []
  );
  const [checkedBy, setLocalCheckedBy] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [apiError, setApiError] = useState<boolean>(false);
  const [isRadioChanged, setIsRadioChanged] = useState<boolean>(false);
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");

  // Memoize allChecklists
  const allChecklists = useMemo(
    () => [
      ...checkItems,
      ...externalChassisFrameItems,
      ...externalFrontItems,
      ...externalRoadSideItems,
      ...externalRearItems,
      ...externalDoorSideItems,
      ...externalTopAndUnderneathItems,
      ...internalEntranceItems,
      ...internalEnsuiteItems,
      ...internalShowerItems,
      ...internalKitchenItems,
      ...internalDinnetteItems,
      ...internalBedroomItems,
    ],
    [
      checkItems,
      externalChassisFrameItems,
      externalFrontItems,
      externalRoadSideItems,
      externalRearItems,
      externalDoorSideItems,
      externalTopAndUnderneathItems,
      internalEntranceItems,
      internalEnsuiteItems,
      internalShowerItems,
      internalKitchenItems,
      internalDinnetteItems,
      internalBedroomItems,
    ]
  );

  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);
  const checkCheckpointRecord = React.useCallback(async () => {
    if (chassisNo) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: "DC" + chassisNo,
          dept_name: "finishing-quality-inspection",
        }).toString();

        const response = await googleDriveAxios.post(
          `/checkpoint-image-record?${queryParams}`
        );
        setActiveCheckpointList(response.data.recorded_checkpoint_list);
      } catch (error) {
        console.error("Error checking images:", error);
      }
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [checkCheckpointRecord]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isRadioChanged) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isRadioChanged]);

  const fetchData = useCallback(async () => {
    if (!chassisNo || chassisNo.length < 4 || chassisNo.length > 5) {
      setCheckItems([]);
      setExternalChassisFrameItems([]);
      setExternalFrontItems([]);
      setExternalRoadSideItems([]);
      setExternalRearItems([]);
      setExternalDoorSideItems([]);
      setExternalTopAndUnderneathItems([]);
      setInternalEntranceItems([]);
      setInternalEnsuiteItems([]);
      setInternalShowerItems([]);
      setInternalKitchenItems([]);
      setInternalDinnetteItems([]);
      setInternalBedroomItems([]);
      setApiError(false);
      setModel("");
      setCompleteStatus("Incomplete");
      setIsRadioChanged(false);
      return;
    }

    setLoading(true);
    setApiError(false);
    setModel("");
    try {
      const response = await axiosPrivate.post(
        `/prepare_finishing_quality_inspection/${chassisNo}`
      );
      const data = response.data;

      const checkItemsData = (data.checkItems || []).map((item: Checklist) => ({
        ...item,
        status: item.status ? item.status.trim().toLowerCase() : "not checked",
      }));
      const externalChassisFrameData = (
        data.externalChassisFrameItems || []
      ).map((item: Checklist) => ({
        ...item,
        status: item.status ? item.status.trim().toLowerCase() : "not checked",
      }));
      const externalFrontData = (data.externalFrontVanItems || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const externalRoadSideData = (data.externalRoadSideVanItems || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const externalRearData = (data.externalRearVanItems || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const externalDoorSideData = (data.externalDoorSideVanItems || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const externalTopAndUnderneathData = (
        data.externalTopRoofAndUnderneathVanItems || []
      ).map((item: Checklist) => ({
        ...item,
        status: item.status ? item.status.trim().toLowerCase() : "not checked",
      }));
      const internalEntranceData = (data.internalEntranceItems || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const internalEnsuiteData = (data.internalEnsuiteItems || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const internalShowerData = (data.internalShowerItems || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const internalKitchenData = (data.internalKitchenItems || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const internalDinnetteData = (data.internalDinnetteItems || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const internalBedroomData = (data.internalBedRoomItems || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );

      setCheckItems(checkItemsData);
      setExternalChassisFrameItems(externalChassisFrameData);
      setExternalFrontItems(externalFrontData);
      setExternalRoadSideItems(externalRoadSideData);
      setExternalRearItems(externalRearData);
      setExternalDoorSideItems(externalDoorSideData);
      setExternalTopAndUnderneathItems(externalTopAndUnderneathData);
      setInternalEntranceItems(internalEntranceData);
      setInternalEnsuiteItems(internalEnsuiteData);
      setInternalShowerItems(internalShowerData);
      setInternalKitchenItems(internalKitchenData);
      setInternalDinnetteItems(internalDinnetteData);
      setInternalBedroomItems(internalBedroomData);
      setLocalCheckedBy(data.checkedBy || "");
      setCheckedBy(data.checkedBy || "");
      setModel(data.model || "");

      const allChecklists = [
        ...checkItemsData,
        ...externalChassisFrameData,
        ...externalFrontData,
        ...externalRoadSideData,
        ...externalRearData,
        ...externalDoorSideData,
        ...externalTopAndUnderneathData,
        ...internalEntranceData,
        ...internalEnsuiteData,
        ...internalShowerData,
        ...internalKitchenData,
        ...internalDinnetteData,
        ...internalBedroomData,
      ];
      const isComplete =
        allChecklists.length > 0 &&
        allChecklists.every(
          (item) =>
            item.status &&
            ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
        );
      setCompleteStatus(isComplete ? "Complete" : "Incomplete");

      let formattedDate = "";
      if (data.createdOn) {
        const date = new Date(data.createdOn);
        if (date.getFullYear() !== 1900) {
          formattedDate = data.createdOn ? data.createdOn.split("T")[0] : "";
        }
      }
      setDate(formattedDate);
    } catch (error: any) {
      if (error.response?.status === 500) {
        setApiError(true);
      } else {
        toast.error("Failed to fetch data.");
      }
    } finally {
      setLoading(false);
    }
  }, [chassisNo, setModel, axiosPrivate, setCheckedBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        chassisNo,
        model: model,
        checkItems: checkItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalChassisFrameItems: externalChassisFrameItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalFrontVanItems: externalFrontItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalRoadSideVanItems: externalRoadSideItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalRearVanItems: externalRearItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalDoorSideVanItems: externalDoorSideItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalTopRoofAndUnderneathVanItems: externalTopAndUnderneathItems.map(
          (item) => ({
            title: item.title,
            notes: item.notes,
            status: item.status,
          })
        ),
        internalEntranceItems: internalEntranceItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        internalEnsuiteItems: internalEnsuiteItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        internalShowerItems: internalShowerItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        internalKitchenItems: internalKitchenItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        internalDinnetteItems: internalDinnetteItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        internalBedRoomItems: internalBedroomItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        checkedBy,
        createdOn: date,
      };

      await toast.promise(
        axiosPrivate.post(
          "/create_or_update_finishing_quality_inspection",
          payload
        ),
        {
          pending: "Saving data...",
          success: "Data saved successfully!",
          error: "Failed to save data.",
        }
      );

      await fetchData();
      setIsRadioChanged(false);

      if (checkedBy) {
        const allChecklists = [
          ...checkItems,
          ...externalChassisFrameItems,
          ...externalFrontItems,
          ...externalRoadSideItems,
          ...externalRearItems,
          ...externalDoorSideItems,
          ...externalTopAndUnderneathItems,
          ...internalEntranceItems,
          ...internalEnsuiteItems,
          ...internalShowerItems,
          ...internalKitchenItems,
          ...internalDinnetteItems,
          ...internalBedroomItems,
        ];

        let itemNoCounter = 1;
        const newReworkItems = allChecklists
          .filter((item) => item.status === "fail")
          .flatMap((item) => {
            const title = item.title || "No Title Available";
            const notesStr =
              item.notes !== null && item.notes !== undefined ? item.notes : "";

            if (notesStr.trim()) {
              if (notesStr.includes(";")) {
                const parts = notesStr
                  .split(";")
                  .map((part) => part.trim())
                  .filter((part) => part);
                return parts.map((note) => ({
                  itemNo: itemNoCounter++,
                  problemDescription: `${title}: ${note}`,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                }));
              } else {
                return [
                  {
                    itemNo: itemNoCounter++,
                    problemDescription: `${title}: ${notesStr}`,
                    comment: "",
                    whoFoundFault: checkedBy,
                    responsible: "",
                    whoDidRework: "",
                    timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                    completed: false,
                  },
                ];
              }
            } else {
              return [
                {
                  itemNo: itemNoCounter++,
                  problemDescription: title,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                },
              ];
            }
          });

        if (newReworkItems.length > 0) {
          const reworkPayload = {
            chassisNo,
            model,
            station_name: "finishing-quality-inspection",
            reworkItems: newReworkItems,
          };

          await axiosPrivate.post(`rework/save`, reworkPayload, {
            params: { rework_type: "finishing-quality-inspection" },
          });
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  const handleNotesChange = (index: number, value: string, type: string) => {
    if (completeStatus === "Complete") return;

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].notes = value;
      setChecklist(updatedData);
    };

    if (type === "check_items") {
      updateChecklist(checkItems, setCheckItems);
    } else if (type === "external_chassis_frame_items") {
      updateChecklist(externalChassisFrameItems, setExternalChassisFrameItems);
    } else if (type === "external_front_items") {
      updateChecklist(externalFrontItems, setExternalFrontItems);
    } else if (type === "external_road_side_items") {
      updateChecklist(externalRoadSideItems, setExternalRoadSideItems);
    } else if (type === "external_rear_items") {
      updateChecklist(externalRearItems, setExternalRearItems);
    } else if (type === "external_door_side_items") {
      updateChecklist(externalDoorSideItems, setExternalDoorSideItems);
    } else if (type === "external_top_and_underneath_items") {
      updateChecklist(
        externalTopAndUnderneathItems,
        setExternalTopAndUnderneathItems
      );
    } else if (type === "internal_entrance_items") {
      updateChecklist(internalEntranceItems, setInternalEntranceItems);
    } else if (type === "internal_ensuite_items") {
      updateChecklist(internalEnsuiteItems, setInternalEnsuiteItems);
    } else if (type === "internal_shower_items") {
      updateChecklist(internalShowerItems, setInternalShowerItems);
    } else if (type === "internal_kitchen_items") {
      updateChecklist(internalKitchenItems, setInternalKitchenItems);
    } else if (type === "internal_dinnette_items") {
      updateChecklist(internalDinnetteItems, setInternalDinnetteItems);
    } else if (type === "internal_bedroom_items") {
      updateChecklist(internalBedroomItems, setInternalBedroomItems);
    }
  };

  const handleRadioChange = (index: number, value: string, type: string) => {
    if (completeStatus === "Complete") return;

    setIsRadioChanged(true);

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].status = value.toLowerCase();
      setChecklist(updatedData);
    };

    if (type === "check_items") {
      updateChecklist(checkItems, setCheckItems);
    } else if (type === "external_chassis_frame_items") {
      updateChecklist(externalChassisFrameItems, setExternalChassisFrameItems);
    } else if (type === "external_front_items") {
      updateChecklist(externalFrontItems, setExternalFrontItems);
    } else if (type === "external_road_side_items") {
      updateChecklist(externalRoadSideItems, setExternalRoadSideItems);
    } else if (type === "external_rear_items") {
      updateChecklist(externalRearItems, setExternalRearItems);
    } else if (type === "external_door_side_items") {
      updateChecklist(externalDoorSideItems, setExternalDoorSideItems);
    } else if (type === "external_top_and_underneath_items") {
      updateChecklist(
        externalTopAndUnderneathItems,
        setExternalTopAndUnderneathItems
      );
    } else if (type === "internal_entrance_items") {
      updateChecklist(internalEntranceItems, setInternalEntranceItems);
    } else if (type === "internal_ensuite_items") {
      updateChecklist(internalEnsuiteItems, setInternalEnsuiteItems);
    } else if (type === "internal_shower_items") {
      updateChecklist(internalShowerItems, setInternalShowerItems);
    } else if (type === "internal_kitchen_items") {
      updateChecklist(internalKitchenItems, setInternalKitchenItems);
    } else if (type === "internal_dinnette_items") {
      updateChecklist(internalDinnetteItems, setInternalDinnetteItems);
    } else if (type === "internal_bedroom_items") {
      updateChecklist(internalBedroomItems, setInternalBedroomItems);
    }
  };

  const checklistsWithSerialNumbers = [
    { data: checkItems, type: "check_items", subHeaderText: "Check" },
    {
      data: externalChassisFrameItems,
      type: "external_chassis_frame_items",
      subHeaderText: "External-Chassis A Frame",
    },
    {
      data: externalFrontItems,
      type: "external_front_items",
      subHeaderText: "External-Front Of Van",
    },
    {
      data: externalRoadSideItems,
      type: "external_road_side_items",
      subHeaderText: "External-Road Side Of Van",
    },
    {
      data: externalRearItems,
      type: "external_rear_items",
      subHeaderText: "External-Rear Of Van",
    },
    {
      data: externalDoorSideItems,
      type: "external_door_side_items",
      subHeaderText: "External-Door side Of Van",
    },
    {
      data: externalTopAndUnderneathItems,
      type: "external_top_and_underneath_items",
      subHeaderText: "External- Top of Roof and Underneath of Van",
    },
    {
      data: internalEntranceItems,
      type: "internal_entrance_items",
      subHeaderText: "Internal-Entrance",
    },
    {
      data: internalEnsuiteItems,
      type: "internal_ensuite_items",
      subHeaderText: "Internal-Ensuite",
    },
    {
      data: internalShowerItems,
      type: "internal_shower_items",
      subHeaderText: "Internal-Shower",
    },
    {
      data: internalKitchenItems,
      type: "internal_kitchen_items",
      subHeaderText: "Internal-Kitchen",
    },
    {
      data: internalDinnetteItems,
      type: "internal_dinnette_items",
      subHeaderText: "Internal-Dinnette",
    },
    {
      data: internalBedroomItems,
      type: "internal_bedroom_items",
      subHeaderText: "Internal-Bedroom",
    },
  ];

  let serialNumberCounter = 0;

  return (
    <Box
      sx={{
        position: "relative",
        filter: loading ? "blur(5px)" : "none",
        pointerEvents: loading ? "none" : "auto",
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className="table__head_row">
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              ></TableCell>
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              >
                S/N
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                CHECKLIST
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                NOTES
              </TableCell>
              <TableCell
                className="table__head_cell"
                sx={{ width: "5%" }}
                align="center"
              >
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box
                    sx={{
                      position: "absolute",
                      top: "60%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : apiError ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="error">Invalid DC Number</Typography>
                </TableCell>
              </TableRow>
            ) : (
              <React.Fragment>
                {checklistsWithSerialNumbers.map((checklistSection) => {
                  const serialNumberStart = serialNumberCounter + 1;
                  serialNumberCounter += checklistSection.data.length;

                  return (
                    <MultiSubheaderTableRowComponent
                      key={checklistSection.type}
                      subHeaderText={checklistSection.subHeaderText}
                      type={checklistSection.type}
                      data={checklistSection.data}
                      handleNotesChange={handleNotesChange}
                      handleRadioChange={handleRadioChange}
                      disabled={completeStatus === "Complete"}
                      allNotChecked={
                        allChecklists.length > 0 &&
                        allChecklists.every(
                          (item) => item.status?.toLowerCase() === "not checked"
                        )
                      }
                      serialNumberStart={serialNumberStart}
                      chassisDCNumber={"DC" + chassisNo}
                      deptName={"finishing-quality-inspection"}
                      activeCheckpointList={activeCheckpointList}
                      checkCheckpointRecord={checkCheckpointRecord}
                    />
                  );
                })}
              </React.Fragment>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked By"
            value={checkedBy}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setLocalCheckedBy(e.target.value);
              setCheckedBy(e.target.value);
            }}
            fullWidth
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setDate(e.target.value);
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
      </Grid>

      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          allChecklists.length === 0 ||
          apiError ||
          loading ||
          (!chassisNo && !model) ||
          completeStatus === "Complete"
        }
      >
        Save
      </Button>
    </Box>
  );
};

export default FinishingQualityInspectionChecklist;
